<template>
    <ul :class="{'four-column-grid': displayFourColumnGrid}">
        <li 
            v-for="(item, index) in cardInfo" 
            :key="index" 
            :class="{'mobile-only': item.mobileOnly}"
        >
            <span>{{ item.title }}</span>
            <span v-if="item.value && item.tooltip">
                <span class="value" v-html="item.value" />
                <tooltip v-if="item.tooltip" class="tooltip" :tooltip-text="item.tooltip" />
            </span>
            <span v-else-if="item.value" class="value" v-html="item.value" />
            <span v-else class="value">-</span>
        </li>
    </ul>
</template>
<script>
import Tooltip from '@/components/Tooltip.vue';
import { pick } from 'lodash';
export default {
    components: {
        Tooltip
    },
    props: {
        data: {
            type: Object,
            required: true
        },
        model: {
            type: Object,
            required: true,
        },
        template: {
            type: String,
            required: false,
            default: 'default'
        },
        showExtraCardInfo: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    computed: {
        cashbackDescription() {
            if (this.model.hasUnlimitedCashback) {
                return this.$format.percentage(this.model.cashbackPercentage);
            }

            return `${this.$format.percentage(this.model.cashbackPercentage)} (max ${this.model.getCashbackLimitString(
                this.$translate('month_short', 'mån'), this.$translate('year_short', 'år')
            )})`;
        },
        hasListTemplate() {
            return this.template !== 'default';
        },
        displayFourColumnGrid() {
            const desktopVisibleItems = Object.values(this.cardInfo).filter(item => !item.mobileOnly);
            return desktopVisibleItems.length % 4 === 0;
        },
        isDebit() {
            return this.model.isDebit;
        },
        isAggregator() {
            return this.model.isAggregator;
        },
        isDebitOrAggregator() {
            return this.isDebit || this.isAggregator;
        },
        notIncludedIcon() {
            return this.getBooleanIcon(false);
        },
        cardInfoConfig() {
            return {
                'card_type': {
                    title: this.$translate('card_type', 'Korttyp'),
                    value: this.getCardTypeValue()
                },
                'yearly_fee': {
                    title: this.$translate('yearly_fee', 'Årskostnad'),
                    value: this.model.yearlyCostHtml,
                    tooltip: this.model.hasFirstYearOffer ? this.$translate('yearly_fee_tooltip') : null
                },
                'effective_rate': {
                    title: this.$translate('effective_rate', 'Effektiv ränta'),
                    value: this.model.isCredit ? this.model.effectiveInterestString : '-'
                },
                'withdrawal_fee': {
                    title: this.$translate('withdrawal_fee', 'Uttagsavgift'),
                    value: this.model.getWithdrawalFeeString()
                },
                'currency_exchange_fee': {
                    title: this.$translate('currency_exchange_fee', 'Valutapåslag'),
                    value: this.model.getCurrencyExchangeFeeString()
                },
                'currency_surcharge': {
                    title: this.$translate('currency_surcharge', 'Valutapåslag'),
                    value: this.model.getCurrencyExchangeFeeString()
                },
                'max_credit': {
                    title: this.$translate('max_credit', 'Maxkredit'),
                    value: this.model.getMaxCreditString(this.$translate('no_max_credit', 'Ingen kreditgräns'))
                },
                'rate_free_days': {
                    title: this.$translate('rate_free_days', 'Räntefria dagar'),
                    value: this.model.interestFreeDays 
                        ? this.model.getInterestFreeDaysString(this.$translate('core_models_rate_free_days', '{days} dagar'))
                        : this.notIncludedIcon,
                },
                'mobile_only_rate_free_days': {
                    mobileOnly: true,
                    title: this.$translate('rate_free_days', 'Räntefria dagar'),
                    value: this.model.interestFreeDays 
                        ? this.model.getInterestFreeDaysString(this.$translate('core_models_rate_free_days', '{days} dagar'))
                        : this.notIncludedIcon,
                },
                'bonus_program': {
                    title: this.$translate('bonus_program', 'Bonusprogram'),
                    value: this.getBooleanIcon(this.model.hasBonusProgram),
                },
                cashback: {
                    title: this.$translate('cashback', 'Cashback'),
                    value: this.model.hasCashback ? this.cashbackDescription : this.notIncludedIcon
                },
                discounts: {
                    title: this.$translate('discounts', 'Rabatter'),
                    value: this.model.hasDiscountProgram 
                        ? this.model.getDiscountString(this.$translate('core_models_discount_percent_in_stores', 'Upp till {percentage} i {count} butiker')) 
                        : this.notIncludedIcon
                },
                'ct_travel_insurance': {
                    title: this.$translate('ct_travel_insurance', 'Reseförsäkring'),
                    value: this.getBooleanIcon(this.model.hasTravelInsurance),
                },
                'airline_bonus': {
                    title: this.$translate('airline_bonus', 'Flygbonus'),
                    value: this.getBooleanIcon(this.model.hasAirlineBonus),
                },
                'fuel_chain_discount': {
                    title: this.$translate('fuel_chain_discount', 'Rabatt på drivmedelskedja'),
                    value: this.model.fuelOffersChain
                },
                'manned_stations_discount': {
                    title: this.$translate('manned_stations_discount', 'Rabatt på bemannade stationer'),
                    value: this.model.fuelDiscountManned 
                        ? this.model.getFuelDiscountMannedString(this.$translate('core_models_fuel_per_liter_discount', '{discount} öre / liter')) 
                        : this.notIncludedIcon
                },
                'unmanned_stations_discount': {
                    title: this.$translate('unmanned_stations_discount', 'Rabatt på obemannade stationer'),
                    value: this.model.fuelDiscountUnmanned 
                        ? this.model.getFuelDiscountUnmannedString(this.$translate('core_models_fuel_per_liter_discount', '{discount} öre / liter')) 
                        : this.notIncludedIcon
                },
                'bonus_cashback_discounts': {
                    title: this.$translate('bonus_cashback_discounts', 'Bonus, Cashback eller Rabatter'),
                    value: this.getBooleanIcon(this.model.hasBonusProgram || this.model.hasCashback || this.model.hasDiscountProgram)
                },
                'shopping_insurances': {
                    title: this.$translate('shopping_insurances', 'Shoppingförsäkringar'),
                    value: this.shoppingInsurancesAmount(this.model) ?? this.notIncludedIcon,
                },
                'mobile_payments': {
                    title: this.$translate('mobile_payments', 'Mobilbetalningar'),
                    value: `<div class="payment-icons">${this.getMobilePaymentsHtmlString(this.model)}</div>`
                },
                'mobile_only_mobile_payments': {
                    mobileOnly: true,
                    title: this.$translate('mobile_payments', 'Mobilbetalningar'),
                    value: `<div class="payment-icons">${this.getMobilePaymentsHtmlString(this.model)}</div>`
                }
            };
        },
        extraCardInfo() {
            if (!(this.$isSweden() || this.$isNorway() || this.$isFinland())) {
                return [];
            }
            if (this.hasListTemplate) {
                return {
                    bonus: ['mobile_only_rate_free_days', 'bonus_program', 'cashback', 'discounts'],
                    travel: ['mobile_only_rate_free_days', 'ct_travel_insurance', 'currency_exchange_fee', 'airline_bonus'],
                    fuel: ['mobile_only_rate_free_days', 'fuel_chain_discount', 'manned_stations_discount', 'unmanned_stations_discount'],
                    shopping: ['mobile_only_rate_free_days', 'bonus_cashback_discounts', 'shopping_insurances', 'rate_free_days']
                }[this.template] || [];
            }
            const defaultCreditCardKeys = this.showExtraCardInfo ? ['rate_free_days', 'bonus_cashback_discounts', 'mobile_payments'] : ['mobile_only_rate_free_days'];
            const defaultDebitCardKeys = this.showExtraCardInfo ? ['mobile_payments'] : ['mobile_only_mobile_payments'];
            return this.isDebitOrAggregator ? defaultDebitCardKeys : defaultCreditCardKeys;
        },
        cardInfoKeys() {
            if (this.$isGermany()) {
                return ['card_type', 'yearly_fee', 'effective_rate', 'withdrawal_fee', 'currency_exchange_fee', 'mobile_payments'];
            }
            else if (this.isDebitOrAggregator) {
                return ['yearly_fee', 'withdrawal_fee', 'currency_exchange_fee', ...this.extraCardInfo];
            }
            return ['max_credit', 'yearly_fee', 'effective_rate', ...this.extraCardInfo];
        },
        cardInfo() {
            return this.getCardInfo(this.cardInfoKeys);
        }
    },
    methods: {
        shoppingInsurancesAmount(model) {
            let shoppingInsurances = 0;
            if (model.hasDeliveryInsurance) {
                shoppingInsurances++;
            }
            if (model.hasExtendedWarranty) {
                shoppingInsurances++;
            }
            if (model.hasPurchaseProtection) {
                shoppingInsurances++;
            }
            if (model.hasPriceGuarantee) {
                shoppingInsurances++;
            }
            return shoppingInsurances;
        },
        getCardTypeValue() {
            if (this.isDebit) {
                return this.$translate('prepaid_card', 'Prepaid');
            }
            if (this.isAggregator) {
                return this.$translate('aggregator_card', 'Prepaid');
            }
            return this.$translate('credit_card', 'Kreditkort');
        },
        getCardInfo(keys) {
            return pick(this.cardInfoConfig, keys);
        },
        getBooleanIcon(value) {
            const icon = this.$formatBooleanIcon(value);
            return `<i class="${icon}"></i>`;
        },
        getImageUrl(path) {
            const siteUrl = process.env.APP_URL;
            return `${siteUrl}/${path}`;
        },
        getMobilePaymentsHtmlString(model) {
            const mobilePayments =[
                {
                    show: model.hasGooglePay,
                    key: 'google'
                },
                {
                    show: model.hasApplePay,
                    key: 'apple'
                },
                {
                    show: model.hasSamsungPay,
                    key: 'samsung'
                },
            ].filter(payment => payment.show);
            
            return mobilePayments.length > 0 
                ? mobilePayments.map(payment => this.$getMobilePaymentLogoHtml(payment.key)).join('') 
                : '-';
        }
    }
};
</script>
<style lang="scss" scoped>
    ul {
        @include grid(1, 3px);
        @include font-size(14px, 0px);
        @include device(pad) {
            @include grid(2, 3px);
        }
        @include device(desktop) {
            @include grid(3, 3px);
        }
        
        &.four-column-grid {
            @include device(desktop) {
                @include grid(4, 3px);
            }
        }

        li {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            row-gap: 0px;
            column-gap: 5px;
            @include spacing(padding, 3);
            text-align: left;
            background: $white;

            @include device(pad) {
                @include spacing(padding, 2);
                flex-direction: column;
                align-items: flex-start;
                row-gap: 10px;
                column-gap: 0px;
                
                &.mobile-only {
                    display: none;
                }
            }

            span {
                font-family: $fontText;
                &.value {
                    font-weight: bold;
                }
                &::v-deep {
                    .check-mark {
                        color: $green;
                    }
                    .cross-mark {
                        color: $red;
                    }
                    .payment-icons {
                        justify-self: center;
                        @include flex(start, start);
                        gap: 0.5rem;
                        img {
                            height: 14px !important;
                        }
                    }
                }
            }
            > span:first-child {
                font-weight: normal;
                font-size: .7em;
                text-transform: uppercase;
            }
            span:last-child {
                text-align: right;
                font-weight: bold;

                @include device(pad) { 
                    text-align: left; 
                }
            }
            span.description {
                line-height: 1rem !important;
            }
        }
    }

    ul.narrow {
        @include device(pad) {
            @include grid(2, 0px);
        }
    }
</style>
